/* eslint @typescript-eslint/no-explicit-any: ['off'] -- "any" types are intentional */
// TS4.0+ -  code from: https://stackoverflow.com/a/58508661

/**
 * "Brief" explanation:
 *
 * Push<T, V> takes a tuple type T and some type V
 * and makes a new tuple with V appended to the end of T.
 *
 * TupleBuilderBuildable<T> takes a tuple type T
 * and represents an object with a build() method that returns a value of type T.
 *
 * TupleBuilderAddable<U, T> takes a union type U
 * and a tuple type T and represents an object with an add() method.
 * This add() method accepts a value of type V that extends U.
 *
 * Conceptually it will remove V from U to get a new union type U',
 * and then push V onto the T tuple to get a new tuple type T'.
 *
 * If U' is empty, add() returns TupleBuilderBuildable<T'>.
 * Otherwise it returns TupleBuilderAddable<U', T'>.
 *
 * This is the important step in the type system:
 * every time you call add(), it shifts one element from the union to the tuple.
 */

type Push<T extends readonly any[], V> = [...T, V];
type ReadonlyTuple<T> = Extract<Readonly<T>, readonly any[]>;

interface TupleBuilderAddable<U, T extends readonly any[]> {
  add<V extends U>(v: V): [U] extends [V] ?
    TupleBuilderBuildable<ReadonlyTuple<Push<T, V>>> :
    TupleBuilderAddable<Exclude<U, V>, ReadonlyTuple<Push<T, V>>>
}
interface TupleBuilderBuildable<T extends readonly any[]> {
  build(): T
}
export function tupleBuilder<U>():
  [U] extends [never] ?
    TupleBuilderBuildable<[]> :
    TupleBuilderAddable<U, []> {
  const tuple: any[] = []
  const ret = {
    add(v: any) {
      tuple.push(v)
      return ret
    },
    build() {
      return tuple
    },
  }
  return ret as any
}
